import React, { useRef, useState, useEffect, useCallback } from "react";
import { FaCloudUploadAlt, FaTimes, FaTrash } from "react-icons/fa";
import Cropper from "react-easy-crop";
import axios from "axios";

const ProfilePicture = ({ handleSubmit, handleFileChange, loading, type, group, onImageDeleted }) => {
    const entityLabel = group?.type === 'company' ? 'company' : 'group';
    const EntityLabel = group?.type === 'company' ? 'Company' : 'Group';

    const [previewImage, setPreviewImage] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const fileInputRef = useRef(null);
    const [deleting, setDeleting] = useState(false);
    const [imageDeleted, setImageDeleted] = useState(false);

    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState(null);

    const currentImage = type === "profile_picture" ? group?.profile_picture : group?.cover_photo;
    const hasCurrentImage = !imageDeleted && currentImage && currentImage.length > 0;

    useEffect(() => {
        return () => {
            if (previewImage) {
                URL.revokeObjectURL(previewImage);
            }
        };
    }, []);

    const resizeImageForCover = (file) => {
        return new Promise((resolve) => {
            const MAX_WIDTH = 1920;
            const MAX_HEIGHT = 640;
            const img = new Image();
            const url = URL.createObjectURL(file);
            img.onload = () => {
                URL.revokeObjectURL(url);
                let { width, height } = img;
                const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height, 1);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob((blob) => {
                    resolve(new File([blob], file.name, { type: 'image/jpeg' }));
                }, 'image/jpeg', 0.85);
            };
            img.src = url;
        });
    };

    const handleFileChangeIN = async (event) => {
        const selectedFile = event.target.files[0];
        if (!selectedFile) return;

        if (type !== "profile_picture") {
            const resized = await resizeImageForCover(selectedFile);
            const previewURL = URL.createObjectURL(resized);
            setPreviewImage(previewURL);
            const fakeEvent = {
                target: {
                    name: type,
                    files: [resized]
                }
            };
            handleFileChange(fakeEvent);
            setCroppedImage(resized);
        } else {
            const previewURL = URL.createObjectURL(selectedFile);
            setPreviewImage(previewURL);
        }
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const generateCroppedImage = async () => {
        if (!previewImage || !croppedAreaPixels) return;

        const image = new Image();
        image.src = previewImage;
        await image.decode();

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;

        ctx.drawImage(
            image,
            croppedAreaPixels.x,
            croppedAreaPixels.y,
            croppedAreaPixels.width,
            croppedAreaPixels.height,
            0,
            0,
            croppedAreaPixels.width,
            croppedAreaPixels.height
        );

        canvas.toBlob((blob) => {
            if (blob) {
                setCroppedImage(blob);
                const fakeEvent = {
                    target: {
                        name: type,
                        files: [new File([blob], "cropped.jpg", { type: "image/jpeg" })]
                    }
                };
                handleFileChange(fakeEvent);
            }
        }, "image/jpeg");
    };

    const handleUpload = async (e) => {
        e.target.classList.add("loadin");
        if (!croppedImage) {
            setMessage("Please crop and confirm the image first!");
            setMessageType("error");
            e.target.classList.remove("loadin");
            return;
        }
        await handleSubmit(e);
        setMessage("Image uploaded successfully!");
        setMessageType("success");
        e.target.classList.remove("loadin");
        setCroppedImage(null);
        setPreviewImage(null);
    };

    const handleDeleteImage = async () => {
        const imageType = type === "profile_picture" ? "profile picture" : "cover photo";
        const confirm = window.confirm(`Are you sure you want to delete the current ${imageType}?`);
        if (!confirm) return;

        setDeleting(true);
        setMessage(null);

        try {
            const formData = new FormData();
            formData.append('id', group.id);
            formData.append(type, null);

            await axios.post(
                'https://buzzinguniverse.com/backend/api/groups/update',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            setImageDeleted(true);
            setMessage(`${type === "profile_picture" ? "Profile picture" : "Cover photo"} deleted successfully!`);
            setMessageType("success");
            if (onImageDeleted) onImageDeleted();
        } catch (error) {
            console.error("Error deleting image:", error);
            setMessage("Failed to delete image. Please try again.");
            setMessageType("error");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="image-upload-container mt-3">
            {/* Current Image Display */}
            {hasCurrentImage && !previewImage && (
                <div className="current-image-container mb-4">
                    <h4 className="mb-2">Current {type === "profile_picture" ? "Profile Photo" : "Cover Photo"}</h4>
                    <div className={`current-image-wrapper ${type === "cover_photo" ? "cover" : "profile"}`}>
                        <img
                            src={`https://buzzinguniverse.com/backend/${currentImage}`}
                            alt={`Current ${type === "profile_picture" ? "profile" : "cover"}`}
                        />
                        <button
                            className="delete-image-btn"
                            onClick={handleDeleteImage}
                            disabled={deleting}
                        >
                            {deleting ? 'Deleting...' : <><FaTrash /> Delete</>}
                        </button>
                    </div>
                </div>
            )}

            {!previewImage && (
                <div className="file-upload-wrapper">
                    <label className="file-upload-box mb-0">
                        <input
                            type="file"
                            accept=".png, .jpeg, .jpg, .webp"
                            ref={fileInputRef}
                            onChange={handleFileChangeIN}
                            className="hidden"
                            hidden
                        />
                        <div className="upload-content">
                            <FaCloudUploadAlt className="upload-icon" />
                            <h5 className="mb-2">{hasCurrentImage ? 'Change' : 'Upload'} {type == "profile_picture" ? "Profile Photo" : "Cover Photo"}</h5>
                        </div>
                    </label>
                </div>
            )}
            {previewImage && !croppedImage && type === "profile_picture" && (
                <div className="cropper-container">
                    <div className="cropper-wrapper">
                        <Cropper
                            image={previewImage}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                        />
                    </div>
                    <button onClick={generateCroppedImage}>Crop Image</button>
                    <button onClick={() => {
                        setPreviewImage(null);
                        setMessage(null);
                    }}>
                        <FaTimes /> Remove
                    </button>
                </div>
            )}

            {croppedImage && (
                <div className="profile-image-preview mt-4 text-center">
                    <img src={URL.createObjectURL(croppedImage)} alt="Cropped Preview" />
                    <button onClick={handleUpload}>{loading ? 'Saving...' : `Set ${type == "profile_picture" ? 'Profile Picture' : 'Cover Photo'}`}</button>
                    <button onClick={() => {
                        setCroppedImage(null);
                        setPreviewImage(null);
                        setMessage(null);
                    }}>
                        <FaTimes /> Remove
                    </button>
                </div>
            )}

            {message && (
                <p className={`message mt-3 ${messageType === "success" ? "success-message" : "error-message"}`}>{message}</p>
            )}
        </div>
    );
};

export default ProfilePicture
